import React, { useState, useEffect } from "react";
import BlogForm from "./components/BlogForm";
import BlogResult from "./components/BlogResult";
import SimpleLogin from "./components/SimpleLogin";
import Dashboard from "./components/Dashboard";
import AdminDashboard from "./components/AdminDashboard";

export default function App() {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState(null);
  const [userType, setUserType] = useState("user");
  const [currentView, setCurrentView] = useState("dashboard"); // dashboard, create, view
  const [selectedBlog, setSelectedBlog] = useState(null);

  useEffect(() => {
    const savedToken = localStorage.getItem("token");
    const savedUserType = localStorage.getItem("userType");
    if (savedToken) {
      setToken(savedToken);
      setUserType(savedUserType || "user");
    }
  }, []);

  const logout = () => {
    setToken(null);
    setUserType("user");
    localStorage.removeItem("token");
    localStorage.removeItem("userType");
    setResult(null);
    setCurrentView("dashboard");
  };

  const handleNewBlog = () => {
    setCurrentView("create");
    setResult(null);
    setSelectedBlog(null);
  };

  const handleViewBlog = (blog) => {
    setSelectedBlog(blog);
    setResult({
      outline: blog.outline,
      seo: blog.seo,
      blog: blog.content
    });
    setCurrentView("view");
  };

  const handleBackToDashboard = () => {
    setCurrentView("dashboard");
    setResult(null);
    setSelectedBlog(null);
  };

  if (!token) {
    return <SimpleLogin setToken={setToken} setUserType={setUserType} />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">AI</span>
              </div>
              <h1 className="text-2xl font-bold text-gray-900">Blog Studio</h1>
            </div>
            
            <div className="flex items-center space-x-4">
              {currentView !== "dashboard" && currentView !== "admin" && (
                <button
                  onClick={handleBackToDashboard}
                  className="text-gray-600 hover:text-gray-900 font-medium transition"
                >
                  ← Back to Dashboard
                </button>
              )}
              {userType === "admin" && (
                <button
                  onClick={() => setCurrentView(currentView === "admin" ? "dashboard" : "admin")}
                  className="text-indigo-600 hover:text-indigo-700 font-medium transition"
                >
                  {currentView === "admin" ? "User Dashboard" : "Admin Panel"}
                </button>
              )}
              <button
                onClick={logout}
                className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition font-medium"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {currentView === "dashboard" && (
          <Dashboard 
            token={token}
            onNewBlog={handleNewBlog}
            onViewBlog={handleViewBlog}
          />
        )}
        
        {currentView === "create" && (
          <div>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Create New Blog</h2>
              <p className="text-gray-600">Generate AI-powered content for your audience</p>
            </div>
            <BlogForm 
              setResult={setResult} 
              setLoading={setLoading} 
              token={token}
              onSuccess={() => setCurrentView("view")}
            />
            {loading && (
              <div className="text-center mt-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                <p className="text-gray-600 mt-2">Generating your blog...</p>
              </div>
            )}
          </div>
        )}
        
        {currentView === "view" && result && (
          <div>
            <div className="mb-6 flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  {selectedBlog ? selectedBlog.title : "Generated Blog"}
                </h2>
                <p className="text-gray-600">
                  {selectedBlog ? `Created ${new Date(selectedBlog.created_at).toLocaleDateString()}` : "Fresh content"}
                </p>
              </div>
              {!selectedBlog && (
                <button
                  onClick={handleBackToDashboard}
                  className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition font-medium"
                >
                  Save & Return to Dashboard
                </button>
              )}
            </div>
            <BlogResult result={result} />
          </div>
        )}
        
        {currentView === "admin" && (
          <AdminDashboard />
        )}
      </main>
    </div>
  );
}